import * as React from "react";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { DrawerContent } from "./components/DrawerContent";
import { DrawerMenuItem, SystemTablesSubItem } from "./components/DrawerMenu";
import { HomeIcon, TruckIcon, ClipboardIcon, FileTextIcon, UsersIcon, BoxIcon, FileIcon, BuildingIcon, SettingsIcon } from "app/icons";
import { Styles } from "./styles";

const drawerWidth = 270;
const DRAWER_SCROLL_KEY = "silagi-drawer-scroll-top";

const Component = () => {
  const styles = Styles();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const drawerPaperRef = React.useRef<HTMLDivElement | null>(null);

  const urlParam = location.pathname.split("/")[1];
  const isOnSystemTablesRoute = location.pathname.startsWith("/system-tables/");
  const isInitialSystemManagementRoute =
    [
      "profile",
      "users",
      "system-tables",
      "price-tables",
      "global-parameters",
      "catalogs",
      "document-templates",
    ].includes(urlParam) || location.pathname.startsWith("/users/");
  const [isSystemManagementOpen, setIsSystemManagementOpen] = React.useState(
    isInitialSystemManagementRoute
  );
  const [isSystemManagementClosedManually, setIsSystemManagementClosedManually] =
    React.useState(false);
  const [isSystemTablesExpanded, setIsSystemTablesExpanded] =
    React.useState(isOnSystemTablesRoute);

  const drawerItems: DrawerMenuItem[] = [
    {
      name: "HOME",
      icon: "apps",
      route: "home",
      to: "/home",
      IconComponent: HomeIcon,
    },
    {
      name: "VEHICLE_STATUS",
      icon: "apps",
      route: "vehicle-statuses",
      to: "/vehicle-statuses",
      IconComponent: TruckIcon,
    },
    {
      name: "WORK_ORDERS",
      icon: "apps",
      route: "work-orders",
      to: "/work-orders",
      IconComponent: ClipboardIcon,
    },
    {
      name: "QUOTES",
      icon: "apps",
      route: "quotes",
      to: "/quotes",
      IconComponent: FileTextIcon,
    },
    {
      name: "CUSTOMERS",
      icon: "apps",
      route: "customers",
      to: "/customers",
      IconComponent: UsersIcon,
    },
    {
      name: "INVENTORY",
      icon: "apps",
      route: "inventory",
      to: "/inventory",
      IconComponent: BuildingIcon,
    },
    {
      name: "FORMS",
      icon: "apps",
      route: "forms",
      to: "/forms",
      IconComponent: FileIcon,
    },
    {
      name: "SUPPLIERS",
      icon: "apps",
      route: "suppliers",
      to: "/suppliers",
      IconComponent: BoxIcon,
    },
    {
      name: "SYSTEM_MANAGEMENT",
      icon: "apps",
      route: "system-management",
      isNested: true,
      IconComponent: SettingsIcon,
    },
  ];

  const systemManagementItems: DrawerMenuItem[] = [
    {
      name: "PROFILE",
      icon: "apps",
      route: "profile",
      to: "/profile",
    },
    {
      name: "USER_MANAGEMENT",
      icon: "apps",
      route: "users",
      to: "/users",
    },
    {
      name: "SYSTEM_TABLES",
      icon: "apps",
      route: "system-tables",
    },
    {
      name: "PRICE_TABLES",
      icon: "apps",
      route: "price-tables",
      to: "/price-tables",
    },
    {
      name: "GLOBAL_PARAMETERS",
      icon: "apps",
      route: "global-parameters",
      to: "/global-parameters",
    },
    {
      name: "CATALOGS",
      icon: "apps",
      route: "catalogs",
      to: "/catalogs",
    },
    {
      name: "DOCUMENT_TEMPLATES",
      icon: "apps",
      route: "document-templates",
      to: "/document-templates",
    },
  ];

  const systemTablesSubItems: SystemTablesSubItem[] = [
    { name: "MANUFACTURERS", route: "manufacturers" },
    { name: "MODEL_PER_MANUFACTURER", route: "model-per-manufacturer" },
    { name: "FRONT_CLEARANCE", route: "front-clearance" },
    { name: "REGISTRATION_INSTRUCTIONS", route: "registration-instructions" },
    { name: "MASTER_MODEL", route: "master-model" },
    { name: "CHASSIS_HEIGHT", route: "chassis-height", showDividerAfter: true },
    { name: "BODY_TYPE", route: "body-type" },
    { name: "FLOOR_TYPE", route: "floor-type" },
    { name: "ROOF_TYPE", route: "roof-type" },
    { name: "HYDRAULIC_TAILGATE_TYPE", route: "hydraulic-tailgate-type" },
    { name: "TIRE_TYPE", route: "tire-type" },
    { name: "SUB_CHASSIS_TYPE", route: "sub-chassis-type" },
    { name: "REAR_WALL_TYPE", route: "rear-wall-type" },
    { name: "ROOF_MATERIAL_TYPE", route: "roof-material-type" },
    {
      name: "HYDRAULIC_TAILGATE_MANUFACTURER",
      route: "hydraulic-tailgate-manufacturer",
    },
    { name: "ITEMS", route: "items", showDividerBefore: true },
  ];

  React.useEffect(() => {
    const currentLanguage = localStorage.getItem("i18nextLng") || "hw";

    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }

    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = currentLanguage === "hw" ? "rtl" : "ltr";
    document.body.dir = currentLanguage === "hw" ? "rtl" : "ltr";
  }, [i18n]);

  const changeLanguage = (language: "hw" | "en") => {
    localStorage.setItem("i18nextLng", language);
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === "hw" ? "rtl" : "ltr";
    document.body.dir = language === "hw" ? "rtl" : "ltr";
    window.location.reload();
  };

  const isItemActive = (route: string) => {
    if (route === "users") {
      return location.pathname.startsWith("/users");
    }

    if (route === "system-tables") {
      return (
        location.pathname === "/system-tables" ||
        location.pathname.startsWith("/system-tables/")
      );
    }

    return urlParam === route;
  };

  const isSubItemActive = (route: string) =>
    location.pathname === `/system-tables/${route}` ||
    location.pathname.startsWith(`/system-tables/${route}/`);

  const isSystemManagementRoute =
    systemManagementItems.some((item) => isItemActive(item.route)) ||
    systemTablesSubItems.some((item) => isSubItemActive(item.route));

  React.useEffect(() => {
    if (!isSystemManagementRoute) {
      setIsSystemManagementClosedManually(false);
      setIsSystemManagementOpen(false);
      return;
    }

    if (!isSystemManagementClosedManually) {
      setIsSystemManagementOpen(true);
    }
  }, [isSystemManagementRoute, isSystemManagementClosedManually]);

  React.useEffect(() => {
    if (isOnSystemTablesRoute) {
      setIsSystemTablesExpanded(true);
    }
  }, [isOnSystemTablesRoute]);

  const persistDrawerScroll = React.useCallback(() => {
    const paper = drawerPaperRef.current;
    if (!paper) return;
    sessionStorage.setItem(DRAWER_SCROLL_KEY, String(paper.scrollTop));
  }, []);

  const restoreDrawerScroll = React.useCallback(() => {
    const paper = drawerPaperRef.current;
    if (!paper) return;

    const saved = sessionStorage.getItem(DRAWER_SCROLL_KEY);
    if (saved == null) return;

    const scrollTop = Number(saved);
    if (Number.isNaN(scrollTop)) return;

    paper.scrollTop = scrollTop;
  }, []);

  React.useLayoutEffect(() => {
    restoreDrawerScroll();

    const frame = requestAnimationFrame(() => {
      restoreDrawerScroll();
    });

    return () => cancelAnimationFrame(frame);
  }, [
    isSystemManagementOpen,
    isSystemTablesExpanded,
    location.pathname,
    restoreDrawerScroll,
  ]);

  const openSystemManagement = () => {
    setIsSystemManagementClosedManually(false);
    setIsSystemManagementOpen(true);
    navigate("/profile");
  };

  const closeSystemManagement = () => {
    setIsSystemManagementClosedManually(true);
    setIsSystemManagementOpen(false);
    navigate("/home");
  };

  const menuItems = isSystemManagementOpen
    ? systemManagementItems
    : drawerItems;

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        component="nav"
        sx={{ width: drawerWidth, flexShrink: 0 }}
        aria-label="sidebar navigation"
      >
        <Drawer
          PaperProps={{
            className: "no-visible-bar",
            ref: drawerPaperRef,
            onScroll: persistDrawerScroll,
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              boxShadow: "none",
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: "none",
              backgroundColor: theme.palette.background.paper,
            },
          }}
          variant="permanent"
          anchor="right"
        >
          <DrawerContent
            styles={styles}
            theme={theme}
            isSystemManagementOpen={isSystemManagementOpen}
            menuItems={menuItems}
            systemTablesSubItems={systemTablesSubItems}
            isSystemTablesExpanded={isSystemTablesExpanded}
            setIsSystemTablesExpanded={setIsSystemTablesExpanded}
            urlParam={urlParam}
            isItemActive={isItemActive}
            isSubItemActive={isSubItemActive}
            openSystemManagement={openSystemManagement}
            closeSystemManagement={closeSystemManagement}
            setIsSystemManagementClosedManually={setIsSystemManagementClosedManually}
            changeLanguage={changeLanguage}
            currentLanguage={i18n.language}
          />
        </Drawer>
      </Box>
    </Box>
  );
};

export const CustomDrawer = React.memo(Component);
